import { ColumnDef, ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import React, { useCallback, useState } from 'react';

import useAlert from '../../Alerts/useAlert';
import ServerSideDataTable from '../../DataTable/ServerSide/datatable.server';
import * as OData from '../../DataTable/ServerSide/datatable.server.ODataHelper';
import DateRange from '../../DateRange';
import { ChangeLog } from '../../Types/ChangeLog';
import { convertDateToJsonFormat, convertLocalTimeZoneToUTC, useDateUtils } from '../../Utilities/useDateUtils';
import { useHttpUtils } from '../../Utilities/useHttpUtils';

function ChangeLogs() {
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { convertUTCToShortDateTimeSecs } = useDateUtils();

    const [data, setData] = useState<ChangeLog[]>([]);
    const [hasNextPage, setHasNextPage] = useState<boolean>(true);

    const saveChangeLogs = (changeLogs: ChangeLog[]) => {
        setData(changeLogs);
    };

    const getFromDate = () => {
        // Start of last month
        return DateTime.now().minus({ week: 1 }).startOf('day');
    };

    const [dateFilter, setDateFilter] = useState({
        from: convertDateToJsonFormat(getFromDate().toUTC()),
        to: null
    });

    const getChangeLogs = useCallback((pagination: PaginationState, filters: ColumnFiltersState, sorting: SortingState) => {
        let params = new URLSearchParams();

        if (dateFilter.from) {
            const utcTime = convertLocalTimeZoneToUTC(dateFilter.from);
            params.append("dateFrom", convertDateToJsonFormat(utcTime));
        }

        if (dateFilter.to) {
            const utcTime = convertLocalTimeZoneToUTC(dateFilter.to);
            params.append("dateTo", convertDateToJsonFormat(utcTime));
        }

        // default sort
        if (sorting.length === 0) {
            sorting = [{ id: "dateChanged", desc: true }];
        }

        OData.addSorting(params, sorting);
        OData.addFilters(params, filters);
        OData.addPaging(params, pagination.pageSize, pagination.pageIndex);

        return fetchDataAuthenticated(`api/v1/changeLog?${params.toString()}`)
            .then(response => {
                if (response.ok) {
                    return response.json();
                }
                throw new Error('Failed to get change log');
            })
            .then(data => {
                saveChangeLogs(data.data);
                setHasNextPage(data.hasNextPage);
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            });
    }, [dateFilter.from, dateFilter.to, fetchDataAuthenticated, setAlert]);

    const tableColumns: ColumnDef<ChangeLog>[] = [
        {
            header: 'Entity Name',
            accessorKey: 'entityName',
            filterFn: 'includesString'
        },
        {
            header: 'Primary Key Value',
            accessorKey: 'primaryKeyValue',
            filterFn: 'includesString'
        },
        {
            header: 'Property Name',
            accessorKey: 'propertyName',
            filterFn: 'includesString'
        },
        {
            header: 'Old Value',
            accessorKey: 'oldValue',
            filterFn: 'includesString'
        },
        {
            header: 'New Value',
            accessorKey: 'newValue',
            filterFn: 'includesString'
        },
        {
            header: 'Date Changed',
            accessorKey: 'dateChanged',
            cell: ({ row }) => {
                return <span>{convertUTCToShortDateTimeSecs(row.original.dateChangedUTC)}</span>;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Account',
            accessorKey: 'account',
            filterFn: 'includesString'
        }
    ];

    return (
        <>
            <DateRange dateFilter={dateFilter} setDateFilter={setDateFilter} />
            <ServerSideDataTable
                tableColumns={tableColumns}
                tableData={data}
                queryFunction={getChangeLogs}
                hasNextPage={hasNextPage}
                showPaging={true} />
        </>
    );
}

export default ChangeLogs;